import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import { PageHeader, DataTable, FilterBar } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Paper, CircularProgress, TextField, MenuItem, Chip } from '@mui/material';
import { GetApp as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const years = Array.from({length: 3}, (_, i) => currentYear - i);

export default function TransportReportsPage() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await api.get('/transport/monthly-report', {
        params: { month, year }
      });
      setReportData(res.data.report || []);
    } catch (err) {
      toast.error('Failed to load monthly report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [month, year]);

  const filteredData = reportData.filter(item => 
    item.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.student?.admissionNumber?.includes(searchTerm)
  );

  const totalFees = reportData.reduce((sum, item) => sum + item.totalFee, 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Usage & Fees Report</>} description={<>Review transport usage and export fee reports.</>} actions={<>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => window.print()}>
                Export / Print
              </Button>
      </>}></PageHeader>

      <SummaryStrip><SummaryMetric>
              <Typography color="text.secondary">Total Generated Rev.</Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                GHS {totalFees.toFixed(2)}
              </Typography>
            </SummaryMetric></SummaryStrip>
<FilterBar>
              <TextField
                select
                label="Month"
                size="small"
                value={month}
                onChange={e => setMonth(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Year"
                size="small"
                value={year}
                onChange={e => setYear(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
              <Button variant="contained" onClick={fetchReport}>Generate</Button>
            </FilterBar>

      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Student Billing Log</Typography>
            <TextField
              size="small"
              placeholder="Search student..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }}
            />
          </Box>

          {loading ? (
            <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
          ) : (
            <DataTable component={Paper} elevation={0} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell align="center">Days Boarded</TableCell>
                    <TableCell align="right">Total Transport Fee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        No usage recorded for this month.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography fontWeight="bold" variant="body2">{row.student?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.student?.admissionNumber}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={`${row.daysUsed} Day(s)`} size="small" color="info" variant="outlined" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          GHS {row.totalFee.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
